"use client";

import { QueryCache, QueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { SpotToast } from "@/components/SpotToast";

/** Retry a failed request up to `max` times, except where retrying cannot
 * help: a missing or refused resource stays so, and retrying held "not found"
 * pages on a skeleton for seconds. A 401 may follow a token refresh and a 408
 * is a timeout, so those still retry; a 429 always does. */
export function retriesRequest(max: number) {
  return (failureCount: number, error: unknown): boolean => {
    const status =
      error instanceof AxiosError ? error.response?.status : undefined;
    if (status === 429) {
      return true;
    }
    if (
      status &&
      status >= 400 &&
      status < 500 &&
      status !== 401 &&
      status !== 408
    ) {
      return false;
    }
    return failureCount < max;
  };
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (
        query.meta?.errorMessage &&
        typeof query.meta.errorMessage === "string"
      ) {
        SpotToast.error(query.meta.errorMessage);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: retriesRequest(3),
      retryDelay: (attemptIndex, error: unknown) => {
        if (error instanceof AxiosError && error?.response?.status === 429) {
          const retryAfter = error.response?.headers["retry-after"];

          if (retryAfter) {
            // Seconds first: `new Date("1")` parses as a date in 2001.
            if (/^\d+$/.test(retryAfter.trim())) {
              return parseInt(retryAfter, 10) * 1000;
            }
            const retryAfterDate = new Date(retryAfter).getTime();
            if (!isNaN(retryAfterDate)) {
              return Math.max(0, retryAfterDate - Date.now());
            }
          }
        }

        return Math.min(1000 * Math.pow(2, attemptIndex), 30000);
      },
    },
  },
});
